import { PRODUCT_DISCOVERY_PROJECT } from '@jira/platform__project-types/src/project-types.tsx';
import type { FieldKey } from '@jira/polaris__domain-field/src/FieldKey.types.tsx';
import { experience } from '@jira/polaris__lib-analytics/src/ConstantsExperience.tsx';
import { wrapWithExperience } from '@jira/polaris__lib-analytics/src/createJpdExperience.tsx';
import { fireCompoundAnalyticsEvent } from '@jira/polaris__lib-analytics/src/fireCompoundAnalyticsEvent.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { StoreActionApi } from '@atlassian/react-sweet-state';
import type { Props, State } from './FieldsUpdate.types.tsx';

export const deleteFieldLocally =
	(fieldKey: FieldKey) =>
	async ({ getState, setState }: StoreActionApi<State>): Promise<void> => {
		const updatedFields = { ...getState().fields };
		delete updatedFields[fieldKey];

		const updatedDecorations = { ...getState().fieldValueDecorations };
		delete updatedDecorations[fieldKey];

		setState({
			fields: updatedFields,
			fieldValueDecorations: updatedDecorations,
		});
	};

export const deleteField =
	(fieldKey: FieldKey, onSuccess?: () => void, onError?: (e: Error) => void) =>
	async (
		{ getState, dispatch }: StoreActionApi<State>,
		{
			onFieldUpdateFailed,
			deletePlay,
			createAnalyticsEvent,
			fieldRemote,
			globalFieldRemote,
			projectId,
		}: Props,
	): Promise<void> => {
		try {
			const currFieldMap = getState().fields;
			const { key, custom, global } = currFieldMap[fieldKey];
			const deletedFieldPlay = currFieldMap[fieldKey]?.play;

			const isGlobalField = custom && global;

			if (!isGlobalField) {
				await fieldRemote.deleteField({
					fieldKey: key,
				});
			} else {
				if (!projectId) {
					throw new Error('project id is not available');
				}

				await wrapWithExperience(experience.globalFieldsAdministration.globalFieldUnassociate, () =>
					globalFieldRemote.removeGlobalFieldFromProject({
						fieldKey: key,
						projectId,
					}),
				);
			}

			// FE state only removal of play; this has no side effects
			if (deletedFieldPlay) {
				deletePlay(deletedFieldPlay);
			}

			dispatch(deleteFieldLocally(fieldKey));

			fireCompoundAnalyticsEvent.FieldConfig.fieldDeletedSuccess(createAnalyticsEvent({}), {
				issueFieldKey: fieldKey,
				issueFieldType: currFieldMap[fieldKey]?.type,
				isGlobalField,
				fieldScope: isGlobalField ? 'global' : 'project',
				projectType: PRODUCT_DISCOVERY_PROJECT,
			});

			onSuccess?.();
			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (e: any) {
			fireCompoundAnalyticsEvent.FieldConfig.fieldDeletedFailure(createAnalyticsEvent({}));
			onError?.(e);
			onFieldUpdateFailed(e);
		}
		return undefined;
	};
